// Pure state model for the qualification-weighting panel. No Angular imports —
// all bucket/dirty/move logic lives here so it is unit-testable under the app's
// node-env vitest (which has no Angular TestBed). The panel component holds one
// QualificationDraftState and delegates every mutation to these functions.

import type {
    MapsQualification,
    MapsQualificationConfigEntry,
    MapsQualificationSource,
} from '../models/maps.types';

/** The four live buckets a card can occupy in the panel. */
export type BucketKey = 'required' | 'veryImportant' | 'important' | 'niceToHave';

/** All bucket keys, in display order — required first, then the three tiers. */
export const BUCKET_KEYS: readonly BucketKey[] = ['required', 'veryImportant', 'important', 'niceToHave'];

/** One qualification card in the panel's working state. */
export interface QualificationDraft {
    /** Server id; null for a custom card the recruiter added (server assigns on Apply). */
    id: string | null;
    /** Stable client identity for *ngFor trackBy and drag identity, unique within a state. */
    localKey: string;
    text: string;
    source: MapsQualificationSource;
    /** Server-echoed gating flag (Test Results 7-15-26): false marks a
     *  judgment-call Required item that displays and ranks but never excludes.
     *  Must round-trip on Apply or the item would start excluding on reruns.
     *  Absent (custom cards) → server defaults to true. */
    gating?: boolean;
    /** For a removed card: the bucket it came from, so Restore returns it there. */
    removedFrom?: BucketKey;
}

/** The panel's full working state. `removed` is the recoverable holding area. */
export interface QualificationDraftState {
    required: QualificationDraft[];
    veryImportant: QualificationDraft[];
    important: QualificationDraft[];
    niceToHave: QualificationDraft[];
    removed: QualificationDraft[];
}

const TIER_TO_BUCKET: Record<string, BucketKey> = {
    veryImportant: 'veryImportant',
    important: 'important',
    niceToHave: 'niceToHave',
};

const BUCKET_TO_TIER: Record<BucketKey, MapsQualificationConfigEntry['bucket']> = {
    required: 'required',
    veryImportant: 'veryImportant',
    important: 'important',
    niceToHave: 'niceToHave',
};

/** Local-key generator: monotonic per state so cards keep a stable identity even
 *  when their server id is null. */
function makeKeyer(): () => string {
    let n = 0;
    return () => `qk-${n++}`;
}

/**
 * Build the panel's working state from the server's applied qualification list.
 * Required quals land in `required`; preferred quals land in their tier bucket;
 * server-removed quals (echoed so they can be restored) land in `removed`.
 * Order within each bucket follows the server order.
 */
export function BuildDraftState(quals: readonly MapsQualification[]): QualificationDraftState {
    const nextKey = makeKeyer();
    const state: QualificationDraftState = {
        required: [],
        veryImportant: [],
        important: [],
        niceToHave: [],
        removed: [],
    };
    for (const q of quals) {
        const draft: QualificationDraft = {
            id: q.id,
            localKey: nextKey(),
            text: q.text,
            source: q.source,
            // Pre-feature payloads lack gating — treat as gating (old behavior).
            gating: q.gating ?? true,
        };
        if (q.removed) {
            // Recover a sensible origin bucket for a Restore action.
            draft.removedFrom = q.classification === 'required' ? 'required' : bucketFromTier(q.tier);
            state.removed.push(draft);
        } else if (q.classification === 'required') {
            state.required.push(draft);
        } else {
            state[bucketFromTier(q.tier)].push(draft);
        }
    }
    return state;
}

function bucketFromTier(tier: MapsQualification['tier']): BucketKey {
    return (tier && TIER_TO_BUCKET[tier]) || 'important';
}

/**
 * Move a card between buckets (or reorder within one) by splice — the same
 * semantics as CDK's moveItemInArray / transferArrayItem, but pure so the panel
 * can drive both drag-drop and (future) keyboard reordering through one path.
 */
export function MoveItem(
    state: QualificationDraftState,
    from: BucketKey,
    to: BucketKey,
    prevIndex: number,
    currIndex: number
): void {
    if (from === to) {
        const list = state[from];
        if (prevIndex < 0 || prevIndex >= list.length) return;
        const [item] = list.splice(prevIndex, 1);
        list.splice(clampIndex(currIndex, list.length), 0, item);
        return;
    }
    const src = state[from];
    if (prevIndex < 0 || prevIndex >= src.length) return;
    const [item] = src.splice(prevIndex, 1);
    const dst = state[to];
    dst.splice(clampIndex(currIndex, dst.length), 0, item);
}

function clampIndex(i: number, len: number): number {
    if (i < 0) return 0;
    if (i > len) return len;
    return i;
}

/** Remove a card from a live bucket into the recoverable `removed` area,
 *  recording where it came from so Restore can return it. */
export function RemoveItem(state: QualificationDraftState, from: BucketKey, index: number): void {
    const list = state[from];
    if (index < 0 || index >= list.length) return;
    const [item] = list.splice(index, 1);
    item.removedFrom = from;
    state.removed.push(item);
}

/** Restore a removed card to the bucket it was removed from (falls back to
 *  required when the origin is unknown). Appended at the end of that bucket. */
export function RestoreItem(state: QualificationDraftState, removedIndex: number): void {
    if (removedIndex < 0 || removedIndex >= state.removed.length) return;
    const [item] = state.removed.splice(removedIndex, 1);
    const target = item.removedFrom ?? 'required';
    delete item.removedFrom;
    state[target].push(item);
}

/** Add a free-text custom qualification to a bucket. Trims; a blank/whitespace
 *  submission is ignored (returns false). Duplicates are allowed. */
export function AddItem(
    state: QualificationDraftState,
    text: string,
    to: BucketKey,
    nextLocalKey: string
): boolean {
    const trimmed = text.trim();
    if (trimmed.length === 0) return false;
    state[to].push({ id: null, localKey: nextLocalKey, text: trimmed, source: 'custom' });
    return true;
}

/**
 * Serialize the working state into the rerun request config. Every live card
 * maps to its bucket. Removed cards that the server already knew (non-null id)
 * are sent with bucket 'removed' so the server echoes them back; removed custom
 * cards (id null — added then deleted before Apply) are omitted, since the
 * server never knew them.
 */
export function ToConfigEntries(state: QualificationDraftState): MapsQualificationConfigEntry[] {
    const entries: MapsQualificationConfigEntry[] = [];
    for (const bucket of BUCKET_KEYS) {
        for (const draft of state[bucket]) {
            entries.push({
                id: draft.id,
                text: draft.text.trim(),
                bucket: BUCKET_TO_TIER[bucket],
                // Round-trip the server's gating flag; omitted (custom) →
                // server default (true). A non-gating required item must stay
                // non-excluding across reruns.
                gating: draft.gating ?? null,
            });
        }
    }
    for (const draft of state.removed) {
        if (draft.id !== null) {
            entries.push({ id: draft.id, text: draft.text.trim(), bucket: 'removed', gating: draft.gating ?? null });
        }
    }
    return entries;
}

/**
 * Whether the working state differs from the last-applied configuration.
 * Order within a bucket never matters — only which bucket each id lives in,
 * plus any custom additions or removals of known ids. Comparison is a
 * Map<id, bucket> diff so reordering is inherently ignored.
 */
export function IsConfigDirty(
    state: QualificationDraftState,
    lastApplied: readonly MapsQualification[]
): boolean {
    // Baseline: bucket (or 'removed') + text for each applied id. The server
    // echoes removed items too, so 'removed' is a valid baseline state — a card
    // that was already removed in lastApplied is NOT a fresh change. Text is
    // tracked so editing a qualification's wording counts as a change.
    const applied = new Map<string, { bucket: BucketKey | 'removed'; text: string }>();
    for (const q of lastApplied) {
        applied.set(q.id, { bucket: q.removed ? 'removed' : classificationBucket(q), text: q.text.trim() });
    }

    // Any custom (id null) card in a live bucket is a new addition → dirty.
    for (const bucket of BUCKET_KEYS) {
        if (state[bucket].some((d) => d.id === null)) return true;
    }
    // A removed card is a change only if it wasn't ALREADY removed in the
    // applied baseline. A custom card added then removed pre-Apply (id null) is
    // a net no-op and is never sent, so it doesn't count.
    const removedIds = new Set<string>();
    for (const draft of state.removed) {
        if (draft.id === null) continue;
        removedIds.add(draft.id);
        if (applied.get(draft.id)?.bucket !== 'removed') return true;
    }

    const seen = new Set<string>();
    for (const bucket of BUCKET_KEYS) {
        for (const draft of state[bucket]) {
            if (draft.id === null) continue; // handled above
            seen.add(draft.id);
            const base = applied.get(draft.id);
            if (!base || base.bucket !== bucket) return true;
            if (base.text !== draft.text.trim()) return true; // text edited
        }
    }
    // A previously-applied non-removed id that vanished from the live buckets
    // and isn't sitting in `removed` → dirty (defensive; normally unreachable).
    for (const [id, base] of applied) {
        if (base.bucket !== 'removed' && !seen.has(id) && !removedIds.has(id)) return true;
    }
    return false;
}

function classificationBucket(q: MapsQualification): BucketKey {
    return q.classification === 'required' ? 'required' : bucketFromTier(q.tier);
}

/** Counts for the panel header / collapsed summary. */
export function SummaryCounts(state: QualificationDraftState): { required: number; preferred: number } {
    return {
        required: state.required.length,
        preferred: state.veryImportant.length + state.important.length + state.niceToHave.length,
    };
}

// ---------------------------------------------------------------------------
// Segmented-toggle view (STN client round 2026-08). The drawer replaced the
// drag-and-drop columns with a per-qualification 5-way toggle; these helpers
// present the SAME QualificationDraftState as a flat card list where each
// card has one segment. 'off' IS the recoverable `removed` area — the
// ratified restore behavior (7-13) survives as "toggle back out of Off",
// and the escape-out-of-Required hatch survives as any other segment.
// ---------------------------------------------------------------------------

/** One toggle position. Maps 1:1 onto the server buckets ('off' → removed). */
export type SegmentKey = 'required' | 'high' | 'medium' | 'low' | 'off';

export const SEGMENT_KEYS: readonly SegmentKey[] = ['required', 'high', 'medium', 'low', 'off'];

export const SEGMENT_LABELS: Record<SegmentKey, string> = {
    required: 'Required',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    off: 'Off',
};

const SEGMENT_TO_BUCKET: Record<Exclude<SegmentKey, 'off'>, BucketKey> = {
    required: 'required',
    high: 'veryImportant',
    medium: 'important',
    low: 'niceToHave',
};

const BUCKET_TO_SEGMENT: Record<BucketKey, SegmentKey> = {
    required: 'required',
    veryImportant: 'high',
    important: 'medium',
    niceToHave: 'low',
};

/** A draft paired with its current segment, for the drawer's flat card list. */
export interface SegmentedDraft {
    draft: QualificationDraft;
    segment: SegmentKey;
}

/**
 * Flatten the state into a stable card list: every draft (live AND removed)
 * ordered by localKey sequence — i.e. server order, with custom additions
 * appended. Toggling a segment never reorders the list.
 */
export function SegmentedDrafts(state: QualificationDraftState): SegmentedDraft[] {
    const rows: SegmentedDraft[] = [];
    for (const bucket of BUCKET_KEYS) {
        for (const draft of state[bucket]) rows.push({ draft, segment: BUCKET_TO_SEGMENT[bucket] });
    }
    for (const draft of state.removed) rows.push({ draft, segment: 'off' });
    return rows.sort((a, b) => localKeyIndex(a.draft) - localKeyIndex(b.draft));
}

function localKeyIndex(draft: QualificationDraft): number {
    const n = Number(draft.localKey.split('-')[1]);
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/**
 * Move one draft (by localKey) to the bucket its new segment maps onto.
 * 'off' routes through RemoveItem so the origin bucket is recorded and the
 * card stays recoverable; leaving 'off' clears that record. No-op when the
 * draft already sits in the target segment or the key is unknown.
 */
export function SetSegment(state: QualificationDraftState, localKey: string, segment: SegmentKey): void {
    for (const bucket of BUCKET_KEYS) {
        const index = state[bucket].findIndex((d) => d.localKey === localKey);
        if (index >= 0) {
            if (segment === 'off') {
                RemoveItem(state, bucket, index);
            } else if (SEGMENT_TO_BUCKET[segment] !== bucket) {
                const [item] = state[bucket].splice(index, 1);
                state[SEGMENT_TO_BUCKET[segment]].push(item);
            }
            return;
        }
    }
    const removedIndex = state.removed.findIndex((d) => d.localKey === localKey);
    if (removedIndex >= 0 && segment !== 'off') {
        const [item] = state.removed.splice(removedIndex, 1);
        delete item.removedFrom;
        state[SEGMENT_TO_BUCKET[segment]].push(item);
    }
}

/** Summary-strip counts: n required · n preferred (high+medium+low) · n off. */
export function SegmentSummary(state: QualificationDraftState): { required: number; preferred: number; off: number } {
    const counts = SummaryCounts(state);
    return { ...counts, off: state.removed.length };
}
